/**
 * Icon and illustration paths for Mental Patterns, emotions, and dashboard.
 * SVGs use currentColor where possible — theme via CSS color on parent.
 */

import iconSmile from "../assets/icons/reactions/smile.svg";
import iconSad from "../assets/icons/reactions/sad.svg";
import iconCry from "../assets/icons/reactions/cry.svg";
import iconHeart from "../assets/icons/reactions/heart.svg";
import iconThumbsUp from "../assets/icons/reactions/thumbs-up.svg";
import iconThumbsDown from "../assets/icons/reactions/thumbs-down.svg";
import iconNeutral from "../assets/icons/reactions/neutral.svg";
import iconConfused from "../assets/icons/reactions/confused.svg";
import iconNervousSweat from "../assets/icons/reactions/nervous-sweat.svg";
import iconMindBlown from "../assets/icons/reactions/mind-blown.svg";
import iconShocked from "../assets/icons/reactions/shocked.svg";
import iconLaugh from "../assets/icons/reactions/laugh.svg";
import iconHundred from "../assets/icons/reactions/hundred.svg";
import iconHug from "../assets/icons/reactions/hug.svg";
import iconPeace from "../assets/icons/reactions/peace.svg";
import iconBrokenHeart from "../assets/icons/reactions/broken-heart.svg";
import iconFacepalm from "../assets/icons/reactions/facepalm.svg";
import iconSleeping from "../assets/icons/reactions/sleeping.svg";
import iconEyeRoll from "../assets/icons/reactions/eye-roll.svg";
import iconGrin from "../assets/icons/reactions/grin.svg";
import iconClap from "../assets/icons/reactions/clap.svg";
import iconOkHand from "../assets/icons/reactions/ok-hand.svg";

import { normalizeReflectionEmotion } from "./emotions.js";

import illusWellness from "../assets/illustrations/hub/wellness.svg";
import illusUnderStress from "../assets/illustrations/hub/under-stress.svg";
import illusAnger from "../assets/illustrations/hub/anger.svg";

import illusPatternOverthinking from "../assets/illustrations/patterns/overthinking.svg";
import illusPatternShuttingDown from "../assets/illustrations/patterns/shutting-down.svg";
import illusPatternPeoplePleasing from "../assets/illustrations/patterns/people-pleasing.svg";
import illusPatternStayingBusy from "../assets/illustrations/patterns/staying-busy.svg";
import illusPatternPullingAway from "../assets/illustrations/patterns/pulling-away.svg";
import illusPatternIrritability from "../assets/illustrations/patterns/irritability.svg";
import illusPatternNumbness from "../assets/illustrations/patterns/numbness.svg";
import illusPatternHyperIndependence from "../assets/illustrations/patterns/hyper-independence.svg";

export const REACTION_ICONS = {
  smile: iconSmile,
  sad: iconSad,
  cry: iconCry,
  heart: iconHeart,
  "thumbs-up": iconThumbsUp,
  "thumbs-down": iconThumbsDown,
  neutral: iconNeutral,
  confused: iconConfused,
  "nervous-sweat": iconNervousSweat,
  "mind-blown": iconMindBlown,
  shocked: iconShocked,
  laugh: iconLaugh,
  hundred: iconHundred,
  hug: iconHug,
  peace: iconPeace,
  "broken-heart": iconBrokenHeart,
  facepalm: iconFacepalm,
  sleeping: iconSleeping,
  "eye-roll": iconEyeRoll,
  grin: iconGrin,
  clap: iconClap,
  "ok-hand": iconOkHand
};

/** Map reflection emotion → reaction icon key (atlas specifics + legacy aliases). */
export const EMOTION_ICON_KEY = {
  Joyful: "laugh",
  Hopeful: "smile",
  Proud: "thumbs-up",
  Loved: "heart",
  Peaceful: "peace",
  Curious: "grin",
  Energetic: "grin",
  Aroused: "grin",
  Valued: "heart",
  Optimistic: "smile",
  Playful: "laugh",
  Content: "smile",
  Accepted: "hug",
  Interested: "grin",
  Strong: "hundred",
  Empowered: "hundred",
  Faithful: "hug",
  Appreciated: "clap",
  Courageous: "thumbs-up",
  Confident: "thumbs-up",
  Successful: "hundred",
  Creative: "grin",
  Respected: "clap",
  Loyal: "hug",
  Focused: "hundred",
  Calm: "peace",
  Connected: "hug",
  Trusting: "hug",
  Thankful: "heart",
  Secure: "peace",
  Thoughtful: "neutral",
  Relieved: "smile",
  Belonging: "hug",
  Sensitive: "neutral",
  Loving: "heart",
  Serene: "peace",
  Relaxed: "peace",
  Safe: "peace",
  Surprised: "shocked",
  Confused: "confused",
  Amazed: "shocked",
  Excited: "grin",
  Embarrassed: "nervous-sweat",
  Indifferent: "sleeping",
  Depressed: "sad",
  Remorseful: "sad",
  Guilty: "sad",
  Fragile: "cry",
  Isolated: "sad",
  Miserable: "cry",
  Bored: "sleeping",
  Ashamed: "cry",
  Sad: "sad",
  Lonely: "sad",
  Disappointed: "sad",
  Hurt: "broken-heart",
  Vulnerable: "nervous-sweat",
  Annoyed: "eye-roll",
  Stressed: "mind-blown",
  Rage: "facepalm",
  Hostile: "facepalm",
  Selfish: "eye-roll",
  Furious: "facepalm",
  Irritated: "facepalm",
  Skeptical: "confused",
  Distrustful: "eye-roll",
  Angry: "facepalm",
  Frustrated: "facepalm",
  Critical: "eye-roll",
  Jealous: "eye-roll",
  Fearful: "nervous-sweat",
  Anxious: "nervous-sweat",
  Overwhelmed: "mind-blown",
  Insecure: "nervous-sweat",
  Disgusted: "eye-roll",
  Disapproving: "eye-roll",
  "Needs Support": "cry",
  Other: "neutral",
  Joy: "laugh",
  Love: "heart",
  Gratitude: "heart",
  Hope: "smile",
  Peace: "peace",
  Contentment: "smile",
  Acceptance: "neutral",
  Stillness: "peace",
  Clarity: "hundred",
  Relief: "smile",
  Curiosity: "grin",
  Anticipation: "grin",
  Pride: "thumbs-up",
  Trust: "hug",
  Admiration: "clap",
  Surprise: "shocked",
  Sadness: "sad",
  Anger: "facepalm",
  Fear: "nervous-sweat",
  Disgust: "eye-roll",
  Guilt: "sad",
  Shame: "cry",
  Embarrassment: "nervous-sweat",
  Jealousy: "eye-roll",
  Envy: "eye-roll",
  Loneliness: "sad",
  Confusion: "confused",
  Overwhelm: "mind-blown",
  Doubt: "confused",
  Frustration: "facepalm",
  Anxiety: "nervous-sweat",
  Rejection: "broken-heart",
  Insecurity: "nervous-sweat",
  Resentment: "facepalm",
  Detachment: "sleeping"
};

export const HUB_ILLUSTRATIONS = {
  wellness: illusWellness,
  underStress: illusUnderStress,
  anger: illusAnger,
  gad7: illusUnderStress
};

export const MENTAL_PATTERN_ILLUSTRATIONS = {
  overthinking: illusPatternOverthinking,
  shutting_down: illusPatternShuttingDown,
  people_pleasing: illusPatternPeoplePleasing,
  staying_busy: illusPatternStayingBusy,
  pulling_away: illusPatternPullingAway,
  irritability: illusPatternIrritability,
  numbness: illusPatternNumbness,
  hyper_independence: illusPatternHyperIndependence
};

export const HUB_CARD_ICONS = {
  wellness: "hundred",
  underStress: "nervous-sweat",
  anger: "facepalm",
  gad7: "nervous-sweat"
};

export const WELLNESS_BAND_ICONS = {
  thriving: "hundred",
  steady: "smile",
  mixed: "neutral",
  strained: "sad",
  needsCare: "cry"
};

export function getWellnessBandIconKey(score) {
  if (score == null) return "neutral";
  if (score >= 75) return "thriving";
  if (score >= 60) return "steady";
  if (score >= 45) return "mixed";
  if (score >= 30) return "strained";
  return "needsCare";
}

export function getReactionIconSrc(key) {
  if (!key) return REACTION_ICONS.neutral;
  return REACTION_ICONS[key] || REACTION_ICONS.neutral;
}

export function getEmotionIconSrc(emotion) {
  const normalized = normalizeReflectionEmotion(emotion);
  const k = EMOTION_ICON_KEY[normalized] || EMOTION_ICON_KEY[emotion] || EMOTION_ICON_KEY.Other;
  return getReactionIconSrc(k);
}
